/* Loading screen controller.
 *
 * The overlay markup lives in index.html and renders before this
 * module ever runs (critical styles inlined in <head>). All this
 * module does is:
 *   1. Watch every <img> that already exists in the DOM at startup.
 *      These are the images the user sees first — back curtains,
 *      floor, audience, custom cursor, ceiling props. While they're
 *      still streaming over the network on a fresh device the scene
 *      looks half-built, which is exactly what the loader hides.
 *   2. Tick the progress bar as each finishes (load OR error — we
 *      don't punish the user for a 404).
 *   3. Fade the overlay out once everything is in, then remove the
 *      element so it doesn't intercept anything.
 *
 * A failsafe timeout dismisses the loader regardless after
 * MAX_WAIT_MS so a single hung image can never strand the player on
 * a black screen.
 */

const MIN_VISIBLE_MS = 450;   // never flash-and-vanish; feels jankier than just showing the scene
const MAX_WAIT_MS    = 8000;  // hard ceiling — dismiss even if some image is still pending

export function initLoadingScreen(): void {
  const overlay = document.getElementById('loading-screen');
  const fillEl  = document.getElementById('loader-progress-fill');
  const hintEl  = document.getElementById('loader-hint');
  if (!overlay || !fillEl) return;

  // Snapshot the <img> elements present at startup. We deliberately
  // don't watch images added later (spawned clouds, picker thumbnails,
  // etc.) — the loader only gates the *initial paint*.
  const images = Array.from(document.querySelectorAll<HTMLImageElement>('img'))
    .filter(img => !!img.getAttribute('src'));

  const total = images.length;
  let done = 0;
  const startedAt = performance.now();
  let dismissed = false;

  const updateProgress = () => {
    const pct = total === 0 ? 100 : Math.round((done / total) * 100);
    fillEl.style.width = `${pct}%`;
    if (hintEl && pct >= 100) hintEl.textContent = 'ready — take your seat';
  };

  const finishOne = () => {
    done++;
    updateProgress();
    if (done >= total) maybeDismiss();
  };

  const maybeDismiss = () => {
    if (dismissed) return;
    dismissed = true;
    overlay.classList.add('complete');
    // Honor a small minimum-visible window so flashes feel intentional.
    const elapsed = performance.now() - startedAt;
    const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
    setTimeout(() => {
      overlay.classList.add('fading');
      // Match the 600ms opacity transition + 900ms curtain slide
      // from styles.css; whichever is longer wins. After that the
      // overlay becomes display:none so it never blocks pointer.
      setTimeout(() => overlay.classList.add('gone'), 950);
    }, wait);
  };

  // Edge case: the page rendered the <img> tags before this module
  // ran, so some may already be `complete` by the time we attach
  // listeners. Count those as done immediately. For the rest, wire
  // load + error so neither path leaves the loader stuck.
  if (total === 0) {
    maybeDismiss();
    return;
  }

  for (const img of images) {
    if (img.complete && img.naturalWidth > 0) {
      finishOne();
      continue;
    }
    if (img.complete && img.naturalWidth === 0) {
      // Already errored before we got here.
      finishOne();
      continue;
    }
    const onSettle = () => {
      img.removeEventListener('load',  onSettle);
      img.removeEventListener('error', onSettle);
      finishOne();
    };
    img.addEventListener('load',  onSettle, { once: true });
    img.addEventListener('error', onSettle, { once: true });
  }

  // Failsafe: a single dropped/extremely slow image can't hold the
  // player hostage. After MAX_WAIT_MS we just dismiss — the scene
  // behind is in better shape than a stuck loader.
  setTimeout(maybeDismiss, MAX_WAIT_MS);

  // Initial paint of 0% (or whatever was already cached).
  updateProgress();
}
